import React, { useEffect, useRef, useState } from "react";
import { IoClose, IoSettingsOutline } from "react-icons/io5";
import { LuVolume1, LuVolume2, LuVolumeOff } from "react-icons/lu";
import { useLiveData } from "../../../../hooks/useLiveData";
import { useRemote } from "../../../../hooks/useRemote";
import { useTV } from "../../../../hooks/useTV";
import useGlobalStore from "../../../../store/global.store";
import useVipMultiviewSoundStore from "../../../../store/useVipMultiviewSoundStore";
import YouTubePlayer from "../YouTubePlayer";
import SyncFullscreen from "./SyncFullscreen";
import useBeaconCore, {
  prettyShow,
  formatCountdown,
  extractYouTubeId,
} from "../useBeaconCore";
import "./BeaconMultiViewDisplay.css";
import {
  TOTAL_PLAYERS_DISPLAY,
  PRE_RECORDED_NAMES,
  TOP_STREAMERS,
  BLOCKED_VIDEO_IDS,
  getBackgroundByEST,
} from "../multi-view.utils";

// All Magic Beacon logic — state machine ticks, polling, BLE wrappers,
// cue runner, picker, peer handoff, dev helpers — lives in useBeaconCore.
// This file is now ONLY the Display-shape concerns: slot autofill from
// liveStreams, embed grid layout, thumbnail bar, double-tap handling,
// background image rotation, and the JSX render.

const DOUBLE_TAP_DELAY = 400;

function BeaconMultiViewDisplay() {
  // ===== Base-app integrations (Display-shape) =====
  const { liveStreams, addEndedStream } = useLiveData();
  const { isMuted, toggleMute, increaseVolume, decreaseVolume } =
    useVipMultiviewSoundStore();
  const { updateView } = useRemote();
  const { isTv } = useTV();

  const setShowBottomBar = useGlobalStore((state) => state.setShowBottomBar);
  const device = useGlobalStore((state) => state.device);
  const beaconDeviceId = device?.magic_beacon_device?._id ?? null;

  // ===== Slot/multiview state (Display owns these — they describe what
  // the embed grid is showing, which is unrelated to Beacon logic). =====
  const [videoIds, setVideoIds] = useState(
    Array(TOTAL_PLAYERS_DISPLAY).fill("")
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [currentVideoIds, setCurrentVideoIds] = useState(
    Array(TOTAL_PLAYERS_DISPLAY).fill("")
  );
  const [recentlyEndedIds, setRecentlyEndedIds] = useState([]);
  const [fullscreenIndex, setFullscreenIndex] = useState(null);

  const [backgroundImage, setBackgroundImage] = useState(getBackgroundByEST());
  const [endedIndexes, setEndedIndexes] = useState([]);
  const [allThumbnails, setAllThumbnails] = useState([]);

  const hasInteracted = useRef(false);
  const lastTapRef = useRef(0);

  // ===== Beacon brain (everything else) =====
  const beacon = useBeaconCore({
    liveStreams,
    videoIds,
    selectedIndex,
    fullscreenIndex,
    isTv,
    setVideoIds,
    setCurrentVideoIds,
    beaconDeviceId,
    isMuted,
    toggleMute,
    increaseVolume,
    decreaseVolume,
  });

  // ===== Lifecycle: hide the bottom bar while this view is mounted,
  // restore on unmount. Background image rotates hourly. =====
  useEffect(() => {
    setShowBottomBar(false);
    const interval = setInterval(() => {
      setBackgroundImage(getBackgroundByEST());
    }, 60000);
    return () => {
      clearInterval(interval);
      setShowBottomBar(true);
    };
  }, []);

  // ===== Display-shape derivations (these still live here because they
  // depend on Display state like recentlyEndedIds, and they feed the
  // thumbnail bar + the liveStreams autofill effects below). =====

  // videoIdToStreamer also exposed by the hook (it derives it from
  // liveStreams internally), so we just read it from there for any
  // JSX that needs to map slot id → name.
  const videoIdToStreamer = beacon.videoIdToStreamer;

  const getSortedThumbnails = (streams) => {
    if (!streams) return [];
    return Object.entries(streams)
      .filter(
        ([name, data]) => data?.videoId && !BLOCKED_VIDEO_IDS.has(data.videoId)
      )
      .map(([name, data]) => ({
        streamer: data.name,
        videoId: data.videoId,
        title: data.title,
        thumbnail: data.thumbnail,
        isTop: TOP_STREAMERS.has(name),
        isLoop: PRE_RECORDED_NAMES.has(name),
      }))
      .sort((a, b) => {
        if (a.isTop !== b.isTop) return b.isTop - a.isTop;
        if (a.isLoop !== b.isLoop) return a.isLoop - b.isLoop;
        return 0;
      });
  };

  const getAvailableVideoIds = (currentPlaying = []) => {
    if (!liveStreams || Object.keys(liveStreams).length === 0) return [];
    const sorted = getSortedThumbnails(liveStreams);
    return sorted
      .map((v) => v.videoId)
      .filter(
        (id) =>
          !currentPlaying.includes(id) &&
          !recentlyEndedIds.includes(id) &&
          !BLOCKED_VIDEO_IDS.has(id)
      );
  };

  // ===== liveStreams autofill — fill empty slots with available streams. =====
  useEffect(() => {
    if (liveStreams && Object.keys(liveStreams).length > 0) {
      const availableIds = getAvailableVideoIds([]);
      const newVideoIds = [...videoIds];
      const currentlyPlaying = new Set(newVideoIds.filter((id) => id));

      for (let i = 0; i < TOTAL_PLAYERS_DISPLAY; i++) {
        if (!newVideoIds[i] && availableIds.length > 0) {
          const nextAvailableId = availableIds.find(
            (id) => !currentlyPlaying.has(id)
          );
          if (nextAvailableId) {
            newVideoIds[i] = nextAvailableId;
            currentlyPlaying.add(nextAvailableId);
          }
        }
      }

      if (JSON.stringify(newVideoIds) !== JSON.stringify(videoIds)) {
        setVideoIds(newVideoIds);
        setCurrentVideoIds(newVideoIds);
      }
    }
  }, [liveStreams]);

  // ===== liveStreams cleanup — drop slot videoIds that have gone offline,
  // then re-fill the freed slots with any available streams. =====
  useEffect(() => {
    if (liveStreams) {
      const currentStreamIds = new Set(
        Object.values(liveStreams)
          .filter((data) => data?.videoId)
          .map((data) => data.videoId)
      );
      setAllThumbnails(getSortedThumbnails(liveStreams));

      const updatedVideoIds = [...videoIds];
      let needsUpdate = false;

      for (let i = 0; i < updatedVideoIds.length; i++) {
        const id = updatedVideoIds[i];
        if (id && !currentStreamIds.has(id)) {
          updatedVideoIds[i] = "";
          needsUpdate = true;
        }
      }

      if (needsUpdate) {
        setVideoIds(updatedVideoIds);
        setCurrentVideoIds(updatedVideoIds);

        const availableIds = getAvailableVideoIds(
          updatedVideoIds.filter((id) => id)
        );
        const finalVideoIds = [...updatedVideoIds];

        for (let i = 0; i < finalVideoIds.length; i++) {
          if (!finalVideoIds[i] && availableIds.length > 0) {
            finalVideoIds[i] = availableIds.shift();
          }
        }

        if (JSON.stringify(finalVideoIds) !== JSON.stringify(updatedVideoIds)) {
          setVideoIds(finalVideoIds);
          setCurrentVideoIds(finalVideoIds);
        }
      }
    }
  }, [liveStreams]);

  // Sync allThumbnails on every liveStreams change (separate from the
  // cleanup effect so it still runs even when no slot needs updating).
  useEffect(() => {
    if (liveStreams) {
      setAllThumbnails(getSortedThumbnails(liveStreams));
    } else {
      setAllThumbnails([]);
    }
  }, [liveStreams]);

  // ===== Selection + double-tap (Display-shape, tied to embed grid) =====
  const handleSelectIndex = (index) => {
    setSelectedIndex(index);
    hasInteracted.current = true;
    setEndedIndexes((prev) => prev.filter((i) => i !== index));
  };

  // Double-tap on a slot:
  //   - if the slot's stream is locked AND we're not already in sync,
  //     enter sync directly (skips the fullscreen-step that would normally
  //     come before, since the user clearly wants to sync the band)
  //   - otherwise toggle fullscreen on the slot
  const handleDoubleTap = (index) => {
    const now = Date.now();
    if (now - lastTapRef.current < DOUBLE_TAP_DELAY) {
      const videoId = videoIds[index];
      const streamerName = videoIdToStreamer[videoId];
      const isLocked =
        beacon.getMagicStateForVideoId(videoId, streamerName) === "proven";
      if (isLocked && !beacon.syncMode.active) {
        lastTapRef.current = 0;
        beacon.enterSyncMode(index);
        return;
      }
      setFullscreenIndex((prev) => (prev === index ? null : index));
      lastTapRef.current = 0;
    } else {
      lastTapRef.current = now;
    }
  };

  const handleVideoSelect = (newVideoId) => {
    if (currentVideoIds.includes(newVideoId)) return;
    const updated = [...videoIds];
    updated[selectedIndex] = newVideoId;
    setVideoIds(updated);
    setCurrentVideoIds(updated);
    setEndedIndexes((prev) => prev.filter((i) => i !== selectedIndex));
  };

  const handleOnVideoEnded = (videoId) => {
    console.log("onVideoEnded", videoId);
    addEndedStream(videoId);
  };

  // 4g: derived effective mute state for the mute icon. When in sync,
  // the icon reflects HLS mute; otherwise it reflects the embed-volume
  // store's mute.
  const effectiveMuted = beacon.syncMode.active ? beacon.hlsMuted : isMuted;

  // Short-aliases to keep the JSX readable. None of these mutate; they
  // just point into the hook return.
  const {
    syncMode,
    mbStatus,
    infoStrip,
    fsBadges,
    streamPicker,
    graceModeActive,
    hlsVolume,
    hlsMuted,
    beaconUserEnabled,
    tinkUserEnabled,
    gearExpanded,
    syncStreamerName,
    syncCues,
    setGearExpanded,
    handleMute,
    handleVolUp,
    handleVolDown,
    handleBeaconToggle,
    handleTinkToggle,
    handleLockedRowClick,
    handleCountdownCtaClick,
    closeStreamPicker,
    closeStreamPickerWithGuard,
    handlePickerTileClick,
    enterSyncMode,
    exitSyncMode,
    peerHandoff,
    findLockedPeer,
    onShowPosTick,
    sendBeaconCmd,
    sendCancel,
    getMagicStateForVideoId,
    getAnyAnchor,
    resolveStreamId,
  } = beacon;

  return (
    <div
      className={`page-container beacon-mv-root ${syncMode.active ? "sync-active" : ""}`}
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* 4g: Volume bar — mirrors multitest2 #volBar exactly.
          Order: X, gear, [cluster: mute, vol+, vol-, tink, MAGIC BEACON].
          Cluster collapsed by default; tap gear to expand.
          Tink visible only in sync mode (CSS rule). */}
      <div
        id="volBar"
        className={`vol-bar ${gearExpanded ? "gear-expanded" : ""}`}
      >
        <button
          id="volClose"
          className="vol-btn vol-close"
          onClick={() => {
            if (syncMode.active) {
              // 4g: in sync mode, X only closes the HLS overlay and
              // returns to multiview — doesn't bail out to VIP picker.
              exitSyncMode("user closed via vol-bar X");
            } else {
              updateView("VIP");
            }
          }}
          title="Close"
        >
          <IoClose size={34} />
        </button>
        <button
          id="volGear"
          className="vol-btn vol-gear"
          onClick={() => setGearExpanded((g) => !g)}
          title="Show controls"
        >
          <IoSettingsOutline size={34} />
        </button>
        <div className="vol-cluster" id="volCluster">
          <button
            id="volMute"
            className={`vol-btn ${effectiveMuted ? "muted" : "unmuted"}`}
            onClick={handleMute}
            title="Toggle mute"
          >
            <LuVolumeOff size={34} />
          </button>
          <button
            id="volUp"
            className="vol-btn"
            onClick={handleVolUp}
            title="Volume up"
          >
            <LuVolume2 size={34} />
          </button>
          <button
            id="volDown"
            className="vol-btn"
            onClick={handleVolDown}
            title="Volume down"
          >
            <LuVolume1 size={34} />
          </button>
          {/* Tink — same 3-circle radar SVG as multitest2 line 2023. */}
          <button
            id="volTink"
            className={`vol-btn vol-tink ${tinkUserEnabled ? "tink-on" : "tink-off"}`}
            onClick={handleTinkToggle}
            title="Toggle Tink Tracker"
          >
            <svg
              viewBox="0 0 24 24"
              width="34"
              height="34"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="9" />
              <circle cx="12" cy="12" r="5.5" />
              <circle cx="12" cy="12" r="2" />
              <path d="M12 12 L20 6" />
            </svg>
          </button>
          {/* MAGIC BEACON — wide rectangle, green ON / red OFF. */}
          <button
            id="beaconToggle"
            className={`vol-btn beacon-btn ${beaconUserEnabled ? "beacon-on" : "beacon-off"}`}
            onClick={handleBeaconToggle}
            title="Toggle Magic Beacon features"
          >
            MAGIC BEACON
          </button>
        </div>
      </div>

      {/* 5a-i / 5a-ii / 5a-iii: mb-status top-right widget.
          Row-based rendering — multitest2 mbRender lockedRows /
          nonLockedRows model. Locked rows come first (green pulse,
          clickable); then non-locked rows (purple). First non-locked
          row gets `mb-row-pending-after-locked` when both buckets
          have entries, for the visual divider. */}
      {(mbStatus.lockedRows.length > 0 ||
        mbStatus.nonLockedRows.length > 0) && (
        <div
          className={`mb-status mb-state-${mbStatus.topState}${
            mbStatus.lockedRows.some((r) => r.type === "locked-cta") ||
            mbStatus.nonLockedRows.some((r) => r.type === "countdown-cta")
              ? " mb-has-cta"
              : ""
          }`}
          style={{
            // Dark gradient over night.jpg for legibility (intentional
            // departure from multitest2, which leaves this badge
            // transparent). Inline because css-loader can't resolve a
            // url() at build time.
            backgroundImage:
              "linear-gradient(160deg, rgba(8,8,24,0.80) 0%, rgba(22,8,40,0.76) 50%, rgba(8,8,24,0.80) 100%), url('/static/vip/night.jpg')",
            backgroundSize: "cover, cover",
            backgroundPosition: "center, center",
          }}
          onClick={() => {
            // Bug 3 fix: tap anywhere on the badge fires the CTA.
            // multitest2 line 7994: uses target.closest to find the row,
            // here we just dispatch by presence: if a locked-cta exists,
            // run that path; else if a countdown-cta exists, run that.
            // Single CTAs dominate — if both exist (rare), locked wins.
            const lockedCta = mbStatus.lockedRows.find(
              (r) => r.type === "locked-cta"
            );
            if (lockedCta) {
              handleLockedRowClick(lockedCta);
              return;
            }
            const countdownCta = mbStatus.nonLockedRows.find(
              (r) => r.type === "countdown-cta"
            );
            if (countdownCta) {
              handleCountdownCtaClick(countdownCta);
            }
          }}
        >
          <div className="mb-status-inner">
            <div className="mb-status-label-wrap">
              <div className="mb-status-label">SYNC YOUR BAND!</div>
            </div>
            <div className="mb-status-divider"></div>
            <div className="mb-status-body">
              {mbStatus.lockedRows.length > 0 && (
                <div
                  className="mb-row mb-row-locked-cta"
                  onClick={() =>
                    handleLockedRowClick(mbStatus.lockedRows[0])
                  }
                >
                  <span className="mb-show">
                    {mbStatus.lockedRows.map((r) => r.names).join(" & ")}:
                  </span>
                  <span className="mb-detail">
                    ✨ Connected! TAP HERE TO SYNC NOW!
                  </span>
                </div>
              )}
              {mbStatus.nonLockedRows.map((row, i) => {
                const pendingClass =
                  mbStatus.lockedRows.length > 0 && i === 0
                    ? " mb-row-pending-after-locked"
                    : "";
                if (row.type === "standby") {
                  return (
                    <div
                      key={`nl-${i}`}
                      className={`mb-row${pendingClass}`}
                    >
                      <span className="mb-detail">{row.detail}</span>
                    </div>
                  );
                }
                if (row.type === "closed") {
                  return (
                    <div
                      key={`nl-${i}`}
                      className={`mb-row${pendingClass}`}
                    >
                      <span className="mb-detail">Opens at</span>
                      <span className="mb-time">{row.opensAt}</span>
                    </div>
                  );
                }
                if (row.type === "syncing") {
                  return (
                    <div
                      key={`nl-${i}`}
                      className={`mb-row${pendingClass}`}
                    >
                      <span className="mb-show">{row.names}:</span>
                      <span className="mb-detail">{row.detail}</span>
                      <span className="mb-time">
                        {formatCountdown(row.remaining)}
                      </span>
                    </div>
                  );
                }
                if (row.type === "countdown-cta") {
                  return (
                    <div
                      key={`nl-${i}`}
                      className={`mb-row mb-row-early${pendingClass}`}
                      onClick={() => handleCountdownCtaClick(row)}
                    >
                      <span className="mb-show">{row.names}:</span>
                      <span className="mb-detail">
                        TAP HERE TO SYNC NOW!
                      </span>
                    </div>
                  );
                }
                if (row.type === "countdown-preview") {
                  return (
                    <div
                      key={`nl-${i}`}
                      className={`mb-row${pendingClass}`}
                    >
                      <span className="mb-show">{row.showName}</span>
                      <span className="mb-detail">starts in</span>
                      <span
                        className={
                          row.urgent
                            ? "mb-time mb-time-urgent"
                            : "mb-time"
                        }
                      >
                        {formatCountdown(Math.max(0, row.msUntilStart))}
                      </span>
                    </div>
                  );
                }
                if (row.type === "upcoming") {
                  return (
                    <div
                      key={`nl-${i}`}
                      className={`mb-row${pendingClass}`}
                    >
                      <span className="mb-prefix">Up Next:</span>
                      <span className="mb-show">{row.names}</span>
                      <span className="mb-at">@</span>
                      <span className="mb-time">{row.formattedTime}</span>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </div>
        </div>
      )}

      <div className="top-row">
        {Array(TOTAL_PLAYERS_DISPLAY)
          .fill()
          .map((_, index) => {
            const id = videoIds[index] || "";
            const isSelected = selectedIndex === index;
            const isFullscreen = fullscreenIndex === index;
            const showEndedOverlay = endedIndexes.includes(index) || !id;
            const streamerName = id ? videoIdToStreamer[id] || "" : "";
            const isPreRecorded =
              streamerName && PRE_RECORDED_NAMES.has(streamerName);
            const isLive =
              id &&
              Object.entries(liveStreams || {}).some(
                ([name, data]) =>
                  data?.videoId === id && !PRE_RECORDED_NAMES.has(name)
              );
            const magicState = getMagicStateForVideoId(id, streamerName);
            const isMagicLocked = magicState === "proven";

            return (
              <div
                key={index}
                className={`video-container video-${index} ${
                  isFullscreen
                    ? index === 0
                      ? "fullscreen-left"
                      : "fullscreen-right"
                    : ""
                }`}
              >
                <div
                  className={`video-wrapper-16x9 ${
                    isSelected ? "selected" : ""
                  } ${isMagicLocked ? "magic-proven" : ""}`}
                  onClick={() => {
                    handleSelectIndex(index);
                    handleDoubleTap(index);
                  }}
                  style={{ backgroundColor: "black" }}
                >
                  <div className="click-capture" />
                  {id && !syncMode.active ? (
                    <YouTubePlayer
                      key={id || index}
                      videoId={id}
                      isSelected={isSelected}
                      onEnded={() => handleOnVideoEnded(id)}
                      onError={() => handleOnVideoEnded(id)}
                    />
                  ) : (
                    <div className="empty-player"></div>
                  )}
                  {streamerName && (
                    <div className="streamer-label" style={{ fontSize: 19 }}>
                      @{streamerName}
                    </div>
                  )}
                  {isMagicLocked ? (
                    <div className="magic-badge" style={{ fontSize: 16 }}>
                      MAGIC BEACON LIVE
                    </div>
                  ) : isPreRecorded ? (
                    <div className="pre-recorded-badge" style={{ fontSize: 16 }}>
                      PRE-RECORDED
                    </div>
                  ) : isLive ? (
                    <div className="live-badge" style={{ fontSize: 16 }}>
                      LIVE
                    </div>
                  ) : null}

                  {/* 5f: embed-fullscreen in-slot badge + peer bubble.
                      Shows when this slot is fullscreen and its stream has
                      an active badge phase (preview/countdown/syncing/
                      locked). Mirrors multitest2 fs-mb-status. */}
                  {isFullscreen && fsBadges[index] && (() => {
                    const b = fsBadges[index];
                    const stateClass =
                      b.phase === "locked"
                        ? "mb-state-locked"
                        : b.phase === "syncing"
                        ? "mb-state-syncing"
                        : "mb-state-standby";
                    return (
                      <>
                        <div
                          className={`mb-status fs-mb-status ${stateClass}${
                            b.tappable ? " mb-has-cta" : ""
                          }`}
                          style={{
                            backgroundImage:
                              "linear-gradient(160deg, rgba(8,8,24,0.80) 0%, rgba(22,8,40,0.76) 50%, rgba(8,8,24,0.80) 100%), url('/static/vip/night.jpg')",
                            backgroundSize: "cover, cover",
                            backgroundPosition: "center, center",
                          }}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            if (!b.tappable) return;
                            // locked phase → regular sync entry
                            enterSyncMode(index);
                          }}
                        >
                          <div className="mb-status-inner">
                            <div className="mb-status-label-wrap">
                              <div className="mb-status-label">{b.label}</div>
                            </div>
                            <div className="mb-status-body">
                              {b.phase === "preview" ||
                              b.phase === "countdown" ? (
                                <>
                                  <div className="mb-row mb-row-subline">
                                    <span className="mb-show">
                                      {b.showName}
                                    </span>
                                    {b.showTimeStr && (
                                      <span className="mb-show-time">
                                        · {b.showTimeStr}
                                      </span>
                                    )}
                                  </div>
                                  <div className="mb-row">
                                    <span className="mb-detail">
                                      {b.detail}
                                    </span>
                                  </div>
                                </>
                              ) : b.phase === "syncing" ? (
                                <div className="mb-row">
                                  <span className="mb-show">
                                    {b.showName}:
                                  </span>
                                  <span className="mb-detail">{b.detail}</span>
                                  <span className="mb-time">
                                    {formatCountdown(b.remaining)}
                                  </span>
                                </div>
                              ) : (
                                <div className="mb-row">
                                  <span className="mb-show">
                                    {b.showName}:
                                  </span>
                                  <span className="mb-detail">{b.detail}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* gold peer bubble — syncing phase + peer locked */}
                        {b.phase === "syncing" && b.peerId && (
                          <div
                            className="sf-peer-bubble fs-peer-bubble"
                            style={{
                              backgroundImage:
                                "linear-gradient(160deg, rgba(10,10,31,0.62) 0%, rgba(26,10,46,0.58) 50%, rgba(10,10,31,0.62) 100%), url('/static/vip/night.jpg')",
                              backgroundSize: "cover, cover",
                              backgroundPosition: "center, center",
                            }}
                            onClick={(ev) => {
                              ev.stopPropagation();
                              peerHandoff(b.peerId);
                            }}
                          >
                            Another Streamer Has Already Synced
                            <br />
                            TAP TO WATCH SOONER!
                          </div>
                        )}
                      </>
                    );
                  })()}
                  {showEndedOverlay && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "black",
                        color: "white",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        fontSize: "2.4rem",
                        padding: "20px",
                        textAlign: "center",
                        zIndex: 20,
                      }}
                    >
                      {id ? (
                        <>
                          This LIVE has ended.
                          <br />
                          Select Another Stream To Continue...
                        </>
                      ) : (
                        <>
                          No Live Streams Currently Available
                          <br />
                          Check Back Soon!
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      </div>

      {/* 5a-iv: mb-info-strip — slim band between embed grid and
          thumbnail scroll. The container ALWAYS renders so its row height
          + margins are reserved in the flex column; only the inner pill's
          visibility toggles. This keeps the thumbnail bar from shifting up
          when the strip hides (e.g. beacon toggled off). Multitest2 2057. */}
      <div className="mb-info-strip">
        {infoStrip.visible && (
          <div className="mb-info-strip-inner">
            <span className="mb-info-prefix">{infoStrip.prefix}</span>
            <span className="mb-info-show">{infoStrip.show}</span>
            {infoStrip.timeStr && (
              <>
                <span className="mb-info-at">@</span>
                <span className="mb-info-time">{infoStrip.timeStr}</span>
              </>
            )}
            {infoStrip.countdownStr && (
              <>
                <span className="mb-info-at">—</span>
                <span
                  className="mb-info-prefix"
                  style={{ marginRight: "6px", marginLeft: 0 }}
                >
                  Starts in
                </span>
                <span className="mb-info-time">
                  {infoStrip.countdownStr}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      <div className="thumbnail-scroll">
        {allThumbnails.map((stream) => {
          const disabled = currentVideoIds.includes(stream.videoId);
          const thumbMagic =
            getMagicStateForVideoId(stream.videoId, stream.streamer) ===
            "proven";
          return (
            <div
              key={stream.videoId}
              className={`thumb-box ${disabled ? "disabled" : ""} ${
                thumbMagic ? "magic-proven" : ""
              }`}
              onClick={() => !disabled && handleVideoSelect(stream.videoId)}
            >
              <img src={stream.thumbnail} alt={stream.title} />
              <div className="thumb-meta">
                <strong
                  style={{
                    maxWidth: "180px",
                    width: "100%",
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: 18,
                  }}
                >
                  @{stream.streamer}
                </strong>
                <div className="badge-row">
                  {thumbMagic ? (
                    <div
                      className="magic-badge"
                      style={{ position: "static", fontSize: 16 }}
                    >
                      SYNC NOW
                    </div>
                  ) : stream.isLoop ? (
                    <div
                      className="pre-recorded-badge"
                      style={{ position: "static", fontSize: 16 }}
                    >
                      PRE-RECORDED
                    </div>
                  ) : (
                    <div
                      className="live-badge"
                      style={{ position: "static", fontSize: 16 }}
                    >
                      WATCH LIVE!
                    </div>
                  )}
                </div>
                {disabled && (
                  <div className="playing-label" style={{ fontSize: 16 }}>
                    (Currently Playing)
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {syncMode.active && (
        <SyncFullscreen
          key={syncMode.streamId}
          streamId={syncMode.streamId}
          isTv={isTv}
          streamerName={syncStreamerName}
          deviceId={beaconDeviceId}
          sendBeaconCmd={sendBeaconCmd}
          sendCancel={sendCancel}
          anchor={syncMode.anchor}
          cues={syncCues}
          graceModeActive={graceModeActive}
          onShowPosTick={onShowPosTick}
          earlyEntry={syncMode.earlyEntry}
          earlyEntryShowId={syncMode.earlyEntryShowId}
          showtimeMs={syncMode.showtimeMs}
          hlsVolume={hlsVolume}
          hlsMuted={hlsMuted}
          tinkUserEnabled={tinkUserEnabled}
          findLockedPeer={findLockedPeer}
          onPeerHandoff={peerHandoff}
          isController={beacon.isController}
          hasHolder={beacon.hasHolder}
          holder={beacon.holder}
          controlSynced={beacon.controlSynced}
          controlKeyReady={beacon.controlKeyReady}
          claimControl={beacon.claimControl}
          onExit={() => exitSyncMode("user closed")}
        />
      )}

      {/* 4h: stream picker modal. Conditionally rendered based on
          streamPicker.open. Density classes scale tile size by item
          count (large ≤3, medium 4-6, small 7-12, tiny ≥13).
          Mirrors multitest2 line 1979-1992 markup + 7748 render. */}
      {streamPicker.open && (
        <div className="stream-picker">
          <div
            className="stream-picker-backdrop"
            onClick={closeStreamPickerWithGuard}
          />
          <div className="stream-picker-card">
            <button
              className="stream-picker-close"
              onClick={closeStreamPicker}
              title="Close"
            >
              ✕
            </button>
            <h2 className="stream-picker-title">{streamPicker.title}</h2>
            <div
              className={`stream-picker-grid ${
                streamPicker.items.length >= 13
                  ? "density-tiny"
                  : streamPicker.items.length >= 7
                  ? "density-small"
                  : streamPicker.items.length >= 4
                  ? "density-medium"
                  : "density-large"
              }`}
            >
              {streamPicker.items.map((item, i) => {
                const isLockedContext =
                  streamPicker.mode === "locked" ||
                  streamPicker.mode === "locked-multi";
                const earlyEntry =
                  streamPicker.mode === "early" ||
                  streamPicker.mode === "early-multi";
                // Show banner: from item.pickerShowId for multi-mode, else
                // from streamPicker.showId for single-show early, else
                // from the anchor's showId for locked tiles.
                const tileShowId =
                  item.pickerShowId ||
                  (earlyEntry
                    ? streamPicker.showId
                    : getAnyAnchor(item.streamId)?.showId || null);
                const showLabel = tileShowId ? prettyShow(tileShowId) : "";
                const ytId = item.meta?.youtubeUrl
                  ? extractYouTubeId(item.meta.youtubeUrl)
                  : null;
                const thumb = ytId
                  ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`
                  : 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 9"><rect width="16" height="9" fill="%23222"/></svg>';
                const bannerClass = isLockedContext
                  ? "stream-picker-tile-show-banner stream-picker-tile-show-banner-locked"
                  : "stream-picker-tile-show-banner";
                return (
                  <div
                    key={`${item.streamId}-${i}`}
                    className="stream-picker-tile"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      handlePickerTileClick(item);
                    }}
                  >
                    {showLabel && (
                      <div className={bannerClass}>{showLabel}</div>
                    )}
                    <img src={thumb} alt="" />
                    <div className="stream-picker-tile-name">
                      @{item.meta?.name || item.streamId}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BeaconMultiViewDisplay;
